import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getMissingRequiredFieldsForProfile,
  type RequiredFieldSpec,
} from "@/lib/requiredFields";

/**
 * Detects which manifest-required fields the given (already profile_completed)
 * member is missing. "Missing" is derived from actual data via the manifest's
 * isMissing predicates — there is no schema-version counter. A future required
 * field automatically flows through here once added to the manifest.
 */
export function useMissingRequiredFields(profileId: string | undefined) {
  const [missing, setMissing] = useState<RequiredFieldSpec[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!profileId) {
      setMissing([]);
      setLoading(false);
      return;
    }
    try {
      const result = await getMissingRequiredFieldsForProfile(
        supabase,
        profileId,
      );
      setMissing(result);
    } catch {
      // Non-fatal: never block the dashboard on a backfill check.
      setMissing([]);
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  return { missing, loading, refresh };
}
